<template>
  <div class="case_detail">
    <div class="banner-section">
      <h1 class="device-name">{{ case_data.name }}</h1>
      <video ref="bannerVideoRef" v-show="isVideoFile(currentBanner)" class="banner-video" muted loop playsinline @canplay="onBannerVideoReady"></video>
      <img v-if="!isVideoFile(currentBanner) && isMobile" :key="currentBanner" class="banner-img" :src="currentBanner" alt="">
      <div v-if="!isVideoFile(currentBanner) && !isMobile" :key="currentBanner" class="banner" :style="{backgroundImage:`url(${currentBanner})`}">
      </div>
      <div class="banner-thumbnails">
        <div
          v-for="(img, index) in banners"
          :key="index"
          class="banner-thumb"
          :class="{ active: activeBannerIndex === index }"
          @click="activeBannerIndex = index"
        >
          <video v-if="isVideoFile(img)" :src="img + '#t=0.2'" preload="metadata" muted class="banner-thumb-video"></video>
          <img v-if="!isVideoFile(img)" :src="img" alt="">
        </div>
      </div>
    </div>
    <div class="case_main">
      <div class="case_title">
        <p class="case_desc">{{ case_data.description }}</p>
      </div>
      <div class="content" v-html="case_data.context"></div>
    </div>

    <div v-if="relatedDevices.length" class="related-section">
      <h2 class="related-title">Related Products</h2>
      <div class="related-wrapper">
        <button class="scroll-arrow scroll-left" @click="scrollRelated(-1)"><span></span></button>
        <div class="related-scroll" ref="relatedScrollRef">
          <div class="related-item" v-for="device in relatedDevices" :key="device.id" @click="goto_device(device.id)">
            <div class="related-item-info">
              <span class="related-item-name">{{ device.name }}</span>
              <span class="related-item-desc">{{ device.description }}</span>
              <button class="related-item-btn" @click.stop="goto_device(device.id)">View Details</button>
            </div>
            <img :src="device.thumbnail" alt="">
          </div>
        </div>
        <button class="scroll-arrow scroll-right" @click="scrollRelated(1)"><span></span></button>
      </div>
    </div>
  </div>
</template>

<script setup name="ProductHardDetailEN">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { getDeviceDetail } from '@/api/device'
import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()
const id = route.query.id
const case_data = ref({})
const relatedDevices = ref([])
const relatedScrollRef = ref(null)
const bannerVideoRef = ref(null)
const activeBannerIndex = ref(0)
const banners = computed(() => {
  const list = []
  if (case_data.value.banner) list.push(case_data.value.banner)
  if (case_data.value.banner2) list.push(case_data.value.banner2)
  if (case_data.value.banner3) list.push(case_data.value.banner3)
  return list.length ? list : [case_data.value.banner || '']
})
const currentBanner = computed(() => {
  return banners.value[activeBannerIndex.value] || ''
})

watch(currentBanner, (url) => {
  if (!bannerVideoRef.value) return
  if (isVideoFile(url)) {
    bannerVideoRef.value.src = url
    bannerVideoRef.value.load()
    bannerVideoRef.value.play().catch(() => {})
  }
})

function onBannerVideoReady(e) {
  e.target.play().catch(() => {})
}

onMounted(() => {
  window.scrollTo(0, 0)
  checkMobile()
  window.addEventListener('resize', checkMobile)
  caserender()
})
onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1024
}

async function caserender() {
  const res = await getDeviceDetail(id)
  case_data.value = res.data
  activeBannerIndex.value = 0
  fetchRelatedDevices()
}

async function fetchRelatedDevices() {
  const ids = case_data.value.deviceIds
  if (!ids) {
    relatedDevices.value = []
    return
  }
  const idArr = ids.split(',').filter(Boolean)
  if (!idArr.length) {
    relatedDevices.value = []
    return
  }
  try {
    const results = await Promise.all(
      idArr.map(id => getDeviceDetail(Number(id)))
    )
    relatedDevices.value = results
      .filter(r => r.code === 200 && r.data)
      .map(r => r.data)
  } catch {
    relatedDevices.value = []
  }
}

function goto_device(id) {
  if (window.location.pathname.startsWith('/en')) {
    router.push(`/en/productharddetail?id=${id}`)
  } else {
    router.push(`/productharddetail?id=${id}`)
  }
}

function isVideoFile(url) {
  if (!url) return false
  return /\.(mp4|webm|ogg|mov|avi)(\?|$)/i.test(url)
}

function scrollRelated(direction) {
  const el = relatedScrollRef.value
  if (!el) return
  const items = el.querySelectorAll('.related-item')
  if (!items.length) return
  const itemWidth = items[0].offsetWidth + parseFloat(getComputedStyle(el).gap || 0)
  const maxScroll = el.scrollWidth - el.clientWidth
  let target = el.scrollLeft + direction * itemWidth
  if (target < 0) target = maxScroll
  else if (target > maxScroll) target = 0
  el.scrollTo({ left: Math.round(target), behavior: 'smooth' })
}
</script>

<style scoped>
.case_detail {
  width: 100%;
}
.banner-section {
  position: relative;
  width: 60%;
  margin: 0 auto 4em;
}
.device-name {
  text-align: center;
  font-weight: 700;
  font-size: 1.5em;
  color: #323232;
  margin-top: 5em;
  margin-bottom: 0.8em;
}
.banner {
  overflow: hidden;
  width: 100%;
  height: 384px;
  background-size: contain;
  background-position: center;
  text-align: left;
  background-repeat: no-repeat;
}
.banner-fade-enter-active,
.banner-fade-leave-active {
  transition: opacity 0.2s ease;
}
.banner-fade-enter-from,
.banner-fade-leave-to {
  opacity: 0;
}
.banner-video {
  width: 100%;
  height: 384px;
  object-fit: contain;
  display: block;
}
.banner-img {
  width: 100%;
  display: block;
}
.banner-thumbnails {
  display: flex;
  justify-content: center;
  gap: 0.8em;
  margin-top: 1em;
}
.banner-thumb {
  position: relative;
  width: 6em;
  height: 4em;
  border: 2px solid transparent;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.3s;
}
.banner-thumb-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.banner-thumb.active {
  border-color: #005BAC;
}
.banner-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.case_main {
  width: 60%;
  margin: 2em auto;
}
.case_main .case_title {
  width: 100%;
  text-align: center;
  margin-bottom: 2em;
}
.case_main .case_title h1 {
  margin-bottom: 0.5em;
}
.case_main .case_desc {
  color: #666;
  font-size: 0.95em;
}
:deep(.content) {
  line-height: 1.8em;
  font-size: 15px;
}
:deep(.content img) {
  max-width: 100%;
  height: auto;
}
:deep(.content p) {
  margin: 0.5em 0;
}
:deep(.content h1),
:deep(.content h2),
:deep(.content h3),
:deep(.content h4) {
  margin: 1em 0 0.5em;
}

/* Related Products */
.related-section {
  width: 60%;
  margin: 3em auto;
}
.related-title {
  font-size: 1.3em;
  font-weight: 600;
  color: #323232;
  margin-bottom: 1em;
}
.related-scroll {
  display: flex;
  gap: 1em;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.5em;
}
.related-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.related-scroll {
  flex: 1;
  display: flex;
  gap: 1em;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.5em;
  scrollbar-width: none;
}
.related-scroll::-webkit-scrollbar {
  display: none;
}
.scroll-arrow {
  flex: 0 0 auto;
  width: 1.2em;
  align-self: stretch;
  border: 1px solid #eee;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.scroll-arrow span {
  display: block;
  width: 0.7em;
  height: 0.7em;
  border-right: 2px solid #999;
  border-bottom: 2px solid #999;
}
.scroll-left span {
  transform: rotate(135deg);
}
.scroll-right span {
  transform: rotate(-45deg);
}
.scroll-arrow:hover span {
  border-color: #005BAC;
}
.scroll-left {
  margin-right: 0.3em;
}
.scroll-right {
  margin-left: 0.3em;
}
.related-item {
  flex: 0 0 calc(50% - 0.5em);
  scroll-snap-align: start;
  cursor: pointer;
  transition: transform 0.3s;
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 0.8em;
  border: 1px solid #eee;
  border-radius: 4px;
}
.related-item:hover {
  transform: translateY(-4px);
}
.related-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}
.related-item-name {
  font-size: 1.1em;
  font-weight: 700;
  color: #323232;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.related-item-btn {
  align-self: flex-start;
  padding: 0.3em 1em;
  font-size: 0.75em;
  color: #005BAC;
  background: transparent;
  border: 1px solid #005BAC;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.3s;
}
.related-item-btn:hover {
  background: #005BAC;
  color: #fff;
}
.related-item-desc {
  font-size: 0.8em;
  color: #888;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.related-item img {
  flex: 0 0 6em;
  width: 6em;
  height: 6em;
  object-fit: contain;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

@media (max-width: 1024px) {
  .banner-section {
    width: 90%;
    margin-bottom: 1em;
  }
  .banner-video,
  .banner-img {
    height: 258px;
    object-fit: contain;
  }
  .device-name {
    font-size: 1.2em;
  }
  .banner-thumbnails {
    gap: 0.5em;
    margin-top: 0.6em;
  }
  .banner-thumb {
    width: 4em;
    height: 2.8em;
  }
  .case_main {
    width: 90%;
  }
  .related-section {
    width: 90%;
    margin: 2em auto;
  }
  .related-item {
    flex: 0 0 100%;
    padding: 0.5em;
    gap: 0.5em;
  }
  .related-item-name {
    font-size: 0.95em;
  }
  .related-item-desc {
    font-size: 0.7em;
    -webkit-line-clamp: 2;
  }
  .related-item img {
    flex: 0 0 4em;
    width: 4em;
    height: 4em;
  }
}
</style>
